import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { seoConfig } from '../utils/seoConfig';

const SEOOptimization = () => {
  const location = useLocation();

  useEffect(() => {
    // Add comprehensive local business structured data
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${seoConfig.baseUrl}/#localbusiness`,
      "name": seoConfig.business.name,
      "alternateName": seoConfig.business.alternateName,
      "description": seoConfig.defaultDescription,
      "url": seoConfig.baseUrl,
      "logo": `${seoConfig.baseUrl}/assets/images/quantumframe-logo.svg`,
      "image": [
        `${seoConfig.baseUrl}/assets/images/quantumframe-office.jpg`,
        `${seoConfig.baseUrl}/assets/images/quantumframe-team.jpg`,
        `${seoConfig.baseUrl}/assets/images/quantumframe-portfolio.jpg`
      ],
      "telephone": seoConfig.business.phone,
      "email": seoConfig.business.email,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "H.No.25 Near Nagar Nigam Store, Fatehgadh",
        "addressLocality": "Bhopal",
        "addressRegion": "Madhya Pradesh",
        "postalCode": "462001",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": seoConfig.location.coordinates.lat,
        "longitude": seoConfig.location.coordinates.lng
      },
      "openingHours": seoConfig.business.hours,
      "priceRange": seoConfig.business.priceRange,
      "paymentAccepted": seoConfig.business.paymentAccepted,
      "currenciesAccepted": "INR",
      "foundingDate": seoConfig.business.foundingDate,
      "numberOfEmployees": {
        "@type": "QuantitativeValue",
        "minValue": 10,
        "maxValue": 50
      },
      "slogan": "99.1% Pure Digital Solutions",
      "knowsAbout": [
        "Web Development",
        "Digital Marketing", 
        "SEO Services",
        "Social Media Marketing",
        "E-commerce Development",
        "WordPress Development",
        "React Development",
        "Content Marketing",
        "PPC Advertising",
        "Brand Development",
        "UI/UX Design",
        "Mobile App Development"
      ],
      "makesOffer": seoConfig.business.services.map(service => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service,
          "provider": {
            "@type": "Organization",
            "name": seoConfig.business.name
          }
        }
      })),
      "areaServed": seoConfig.business.areaServed.map(area => ({
        "@type": "City",
        "name": area
      })),
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": seoConfig.location.coordinates.lat,
          "longitude": seoConfig.location.coordinates.lng
        },
        "geoRadius": "100000"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": seoConfig.location.rating.value,
        "reviewCount": seoConfig.location.rating.count,
        "bestRating": seoConfig.location.rating.bestRating,
        "worstRating": seoConfig.location.rating.worstRating
      },
      "review": [
        {
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": "Rajesh Sharma"
          },
          "datePublished": "2024-06-15",
          "description": "QuantumFrame Studio delivered an outstanding website for my business. Their team is professional, skilled, and truly understands digital marketing. Highly recommended for web development in Bhopal!",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          }
        },
        {
          "@type": "Review", 
          "author": {
            "@type": "Person",
            "name": "Priya Patel"
          },
          "datePublished": "2024-07-22",
          "description": "Excellent SEO services! Our website ranking improved significantly within 3 months. QuantumFrame Studio is the best digital marketing agency in Bhopal.",
          "reviewRating": {
            "@type": "Rating", 
            "ratingValue": "5",
            "bestRating": "5"
          }
        },
        {
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": "Amit Gupta"
          },
          "datePublished": "2024-08-10",
          "description": "Their e-commerce development service is top-notch. Created a beautiful online store with seamless payment integration. Great experience working with QuantumFrame Studio!",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5", 
            "bestRating": "5"
          }
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Digital Services Portfolio",
        "itemListElement": [
          {
            "@type": "OfferCatalog",
            "name": "Web Development Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Custom Website Development",
                  "description": "Professional custom website development using modern technologies like React, Node.js, and WordPress"
                },
                "price": "25000",
                "priceCurrency": "INR"
              },
              {
                "@type": "Offer", 
                "itemOffered": {
                  "@type": "Service",
                  "name": "E-commerce Development",
                  "description": "Complete e-commerce solutions with payment integration and inventory management"
                },
                "price": "50000",
                "priceCurrency": "INR"
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service", 
                  "name": "WordPress Development",
                  "description": "Custom WordPress websites with theme development and plugin customization"
                },
                "price": "20000",
                "priceCurrency": "INR"
              }
            ]
          },
          {
            "@type": "OfferCatalog",
            "name": "Digital Marketing Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "SEO Optimization",
                  "description": "Comprehensive SEO services to improve search engine rankings and organic traffic"
                },
                "price": "15000",
                "priceCurrency": "INR"
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Social Media Marketing",
                  "description": "Strategic social media management and advertising campaigns across all platforms"
                },
                "price": "12000", 
                "priceCurrency": "INR"
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Content Marketing",
                  "description": "Professional content creation, copywriting, and content strategy development"
                },
                "price": "10000",
                "priceCurrency": "INR"
              }
            ]
          }
        ]
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": seoConfig.business.phone,
        "contactType": "customer service",
        "email": seoConfig.business.email,
        "availableLanguage": ["English", "Hindi"],
        "hoursAvailable": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      },
      "sameAs": [
        seoConfig.social.facebook,
        seoConfig.social.instagram,
        seoConfig.social.linkedin,
        seoConfig.social.twitter,
        seoConfig.social.youtube
      ]
    };

    // Add service-specific structured data based on current page
    let serviceSchema = null;
    if (location.pathname === '/services' || location.pathname.includes('service')) {
      serviceSchema = {
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        "name": "QuantumFrame Studio - Digital Services",
        "provider": {
          "@type": "Organization",
          "name": seoConfig.business.name
        },
        "serviceType": "Web Development and Digital Marketing",
        "description": "Comprehensive digital services including web development, SEO, social media marketing, and e-commerce solutions",
        "areaServed": {
          "@type": "State",
          "name": "Madhya Pradesh"
        },
        "hasOfferCatalog": localBusinessSchema.hasOfferCatalog
      };
    }

    // Add FAQ structured data for better SERP features
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What makes QuantumFrame Studio the best web development company in Bhopal?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "QuantumFrame Studio stands out as Bhopal's leading web development company due to our 5+ years of experience, expert team of developers, 99.1% client satisfaction rate, comprehensive digital solutions, and proven track record of delivering successful projects across Madhya Pradesh. We specialize in modern technologies like React, WordPress, and provide end-to-end digital marketing services."
          }
        },
        {
          "@type": "Question", 
          "name": "Does QuantumFrame Studio provide SEO services in Bhopal and nearby cities?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, QuantumFrame Studio provides comprehensive SEO services in Bhopal, Indore, Jabalpur, Gwalior, and across Madhya Pradesh. Our SEO experts specialize in local SEO, technical SEO, content optimization, keyword research, and link building to improve your website's search engine rankings and organic traffic."
          }
        },
        {
          "@type": "Question",
          "name": "What are the web development costs at QuantumFrame Studio?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "QuantumFrame Studio offers competitive web development pricing starting from ₹15,000 for basic business websites, ₹25,000 for custom websites, ₹50,000 for e-commerce solutions, and up to ₹2,00,000+ for complex enterprise applications. We provide detailed quotes based on your specific requirements and offer flexible payment plans."
          }
        },
        {
          "@type": "Question",
          "name": "How long does it take to complete a website development project?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Website development timelines at QuantumFrame Studio typically range from 2-3 weeks for basic websites, 4-6 weeks for custom websites with advanced features, and 8-12 weeks for complex e-commerce or enterprise solutions. We provide detailed project timelines and keep clients updated throughout the development process."
          }
        },
        {
          "@type": "Question",
          "name": "Do you provide digital marketing services along with web development?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, QuantumFrame Studio is a full-service digital agency offering complete digital marketing services including SEO optimization, social media marketing, content marketing, PPC advertising, email marketing, and brand development. We provide integrated solutions to help your business grow online."
          }
        }
      ]
    };

    // Add all structured data to the page
    const schemas = [localBusinessSchema, faqSchema];
    if (serviceSchema) schemas.push(serviceSchema);

    schemas.forEach((schema, index) => {
      const scriptId = `seo-schema-${index}`;
      let existingScript = document.getElementById(scriptId);
      
      if (existingScript) {
        existingScript.innerHTML = JSON.stringify(schema);
      } else {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = scriptId;
        script.innerHTML = JSON.stringify(schema);
        document.head.appendChild(script);
      }
    });

    // Add additional meta tags for better SEO
    const additionalMetaTags = [
      // Business-specific meta tags
      { name: 'business:contact_data:street_address', content: 'H.No.25 Near Nagar Nigam Store, Fatehgadh' },
      { name: 'business:contact_data:locality', content: 'Bhopal' },
      { name: 'business:contact_data:region', content: 'Madhya Pradesh' },
      { name: 'business:contact_data:postal_code', content: '462001' },
      { name: 'business:contact_data:country_name', content: 'India' },
      { name: 'business:contact_data:phone_number', content: seoConfig.business.phone },
      { name: 'business:contact_data:website', content: seoConfig.baseUrl },
      
      // Location-specific meta tags
      { name: 'location', content: 'Bhopal, Madhya Pradesh, India' },
      { name: 'distribution', content: 'local' },
      { name: 'coverage', content: 'Worldwide' },
      { name: 'target', content: 'all' },
      { name: 'audience', content: 'all' },
      { name: 'subject', content: 'Web Development, Digital Marketing, SEO Services' },
      { name: 'language', content: 'English, Hindi' },
      { name: 'revisit-after', content: '7 days' },
      { name: 'rating', content: 'General' },
      
      // Industry-specific meta tags
      { name: 'industry', content: 'Information Technology' },
      { name: 'category', content: 'Web Development, Digital Marketing, SEO' },
      { name: 'classification', content: 'Business' },
      { name: 'owner', content: 'QuantumFrame Studio' },
      { name: 'copyright', content: `© ${new Date().getFullYear()} QuantumFrame Studio. All rights reserved.` },
      { name: 'doc-type', content: 'Web Page' },
      { name: 'doc-class', content: 'Living Document' },
      
      // Additional Open Graph tags for better social sharing
      { property: 'business:hours:day', content: 'monday,tuesday,wednesday,thursday,friday,saturday' },
      { property: 'business:hours:start', content: '09:00' },
      { property: 'business:hours:end', content: '18:00' },
      { property: 'place:location:latitude', content: seoConfig.location.coordinates.lat },
      { property: 'place:location:longitude', content: seoConfig.location.coordinates.lng },
      
      // Enhanced Twitter Card data
      { name: 'twitter:label1', content: 'Services' },
      { name: 'twitter:data1', content: 'Web Development, Digital Marketing, SEO' },
      { name: 'twitter:label2', content: 'Location' },
      { name: 'twitter:data2', content: 'Bhopal, Madhya Pradesh' }
    ];

    additionalMetaTags.forEach(tag => {
      let element = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        if (tag.name) element.setAttribute('name', tag.name);
        if (tag.property) element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });

    return () => {
      // Cleanup schemas on unmount
      schemas.forEach((_, index) => {
        const script = document.getElementById(`seo-schema-${index}`);
        if (script) script.remove();
      });
    };
  }, [location]);

  return null;
};

export default SEOOptimization;
